<template>
  <div v-if="notification.length > 0" class="p-h01-notifications">
    <div class="p-h01-notifications__inner">
      <div class="p-h01-notifications__header">
        <p class="p-h01-notifications__header__title">ポイント通知</p>
        <button
          class="p-h01-notifications__header__button"
          @click="readAllNotification"
        >
          <i class="p-h01-notifications__check">
            <IconBase
              :width="16"
              :height="16"
              view-box="0 0 16 16"
              icon-color="#1F2021"
            >
              <IconCheckbox />
            </IconBase>
          </i>
          <span class="p-h01-notifications__header__button__text"
            >すべてを既読にする</span
          >
        </button>
      </div>
      <ul class="p-h01-notifications__list">
        <li
          v-for="({ id, date, subject1, subject2, point, type }, key) in notification"
          :key="id"
          class="p-h01-notifications__list__item"
        >
          <div
            class="p-h01-notifications-item"
            :class="{
              'p-h01-notifications-item--clickable': type === 30
            }"
            @click="showNotificationDetails(type, key)"
          >
            <div class="p-h01-notifications-item__detail">
              <div class="p-h01-notifications-item__date">{{ date }}</div>
              <div class="p-h01-notifications-item__body">
                <p>
                  <span data-translate-skip>{{ subject1 }}</span>{{ subject2 }}
                </p>
              </div>
              <div
                class="p-h01-notifications-item__point"
                :class="`p-h01-notifications-item__point--${pointSign(point)}`"
                data-translate-skip
              >
                <span class="p-h01-notifications-item__point__sign">{{
                  point.sign ? "+" : "−"
                }}</span>
                <span
                  class="p-h01-notifications-item__point__num"
                  >{{ addComma(point.point) }}</span
                >
                <span class="p-h01-notifications-item__point__unit">{{
                  unit
                }}</span>
              </div>
            </div>
            <div class="p-h01-notifications-item__read" @click.stop>
              <button
                class="p-h01-notifications-item__read__button"
                @click="closeNotification(id)"
              >
                <i class="p-h01-notifications__check">
                  <IconBase
                    :width="16"
                    :height="16"
                    view-box="0 0 16 16"
                    icon-color="#1F2021"
                  >
                    <IconCheckbox />
                  </IconBase>
                </i>
                <span class="p-h01-notifications-item__read__text"
                  >既読にする</span
                >
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <ModalNotifyThanksPointDenial
      v-if="modalName === 'notifyThanksPointDenial'"
      :user="notifyDenialUser"
      :transaction="notifyDenialTransaction"
      :reason="notifyDenialReason"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import IconCheckbox from "@/common/components/icons/IconCheckbox.vue";
import IconBase from "@/common/components/atoms/IconBase.vue";
import ModalNotifyThanksPointDenial from "@/general/components/organisms/ModalNotifyThanksPointDenial.vue";

import commons from "@/common/store/modules/commons";
import modalState from "@/common/store/modules/modalState";
import { getThanksPointName } from "@/common/assets/js/utils/pointName";
import CONST_DATA from "@/general/mixins/const/constData";

import {
  getPointNotification,
  markNotificationAsRead,
  updateAllPointNotification
} from "@/general/store/api";

@Options({
  components: {
    IconCheckbox,
    IconBase,
    ModalNotifyThanksPointDenial
  }
})
export default class TheH01Notification extends Vue {
  notification: Array<any> = [];

  notifyDenialUser: any = {};
  notifyDenialTransaction: any = {};
  notifyDenialReason = "";

  get modalName() {
    return modalState.modalName;
  }

  get unit() {
    return commons?.OrgPageSpecific?.point_name;
  }

  get thanksPointName() {
    return getThanksPointName(CONST_DATA.POINT_NAME.THANKS_POINT_DEFAULT_NAME2);
  }

  async getNotification() {
    const response = await getPointNotification(
      "false",
      "-created_at",
      undefined,
      100,
      "true",
      undefined,
      undefined
    );

    const approvedTargetStr = "へのサンクスメッセージが承認されました";
    const approvedTargetRegex = new RegExp(
      approvedTargetStr + "(?!.*" + approvedTargetStr + ")",
      "g"
    );
    const approvedReplaceStr =
      "への" + this.thanksPointName + "メッセージが承認されました";

    const rejectedTargetStr = "へのサンクスメッセージが否認されました";
    const rejectedTargetRegex = new RegExp(
      rejectedTargetStr + "(?!.*" + rejectedTargetStr + ")",
      "g"
    );
    const rejectedReplaceStr =
      "への" + this.thanksPointName + "メッセージが否認されました";

    // 'notification_type', models.PositiveSmallIntegerField(choices=[(10, 'Grant Points cancelled'), (20, 'Thanks points approved'), (30, 'Thanks points disapproved'), (40, 'Received Grant Points')
    this.notification =
      response?.results?.map((result: any) => {
        const { pk, from_user, transaction, notification_type } = result;
        const sign =
          notification_type === 30 || notification_type === 40 ? true : false;
        const user = from_user;

        let targetNum = 0;
        if (notification_type === 10) {
          if (
            result.grant_criteria_id === 0 ||
            result.register_process === "SCR-UPL-0060"
          ) {
            targetNum = result.content.lastIndexOf(
              "への付与（配布）が取り消されました"
            );
          } else {
            targetNum = result.content.lastIndexOf(
              "の付与（配布）が取り消されました"
            );
          }
        } else if (notification_type === 20) {
          result.content = result.content.replace(
            approvedTargetRegex,
            approvedReplaceStr
          );
          targetNum = result.content.lastIndexOf(approvedReplaceStr);
        } else if (notification_type === 30) {
          result.content = result.content.replace(
            rejectedTargetStr,
            rejectedReplaceStr
          );
          targetNum = result.content.lastIndexOf(rejectedReplaceStr);
        }

        return {
          id: pk,
          type: notification_type,
          date: this.$dayjs
            .utc(result.created_at)
            .locale("ja")
            .format("YYYY/MM/DD HH:mm"),
          state: result.notification_type_display,
          subject: result.content,
          subject1: result.content.substring(0, targetNum),
          subject2: result.content.substring(targetNum),
          point: {
            point: result.points,
            sign
          },
          checked: false,
          user,
          transaction
        };
      }) || [];
  }

  async closeNotification(id: number) {
    for (let i = 0; i < this.notification.length; i++) {
      if (this.notification[i].id === id) {
        this.notification.splice(i, 1);
        await markNotificationAsRead(id);
      }
    }
  }

  async readAllNotification() {
    const ids = this.notification.map((item: any) => item.id);
    this.notification = [];
    await updateAllPointNotification(ids);
  }

  addComma(value: any) {
    const number = Number(value);
    if (Number.isNaN(number)) {
      return value;
    }
    return number.toLocaleString();
  }

  pointSign(point: any) {
    return point.sign ? "plus" : "minus";
  }

  showNotificationDetails(type: number, index: number) {
    if (type === 30) {
      this.notifyDenialUser = this.notification[index].user;
      this.notifyDenialTransaction = this.notification[index].transaction;
      this.notifyDenialReason = this.notification[index].subject;

      modalState.changeModal("notifyThanksPointDenial");
    }
  }

  created() {
    this.getNotification();
  }
}
</script>

<style lang="scss" scoped>
.p-h01-notifications {
  @include large-pc {
    padding: 0 rem(8);
  }
  @include large-sp {
    padding: 0 rem(16);
  }
  background-color: $common-white;
  border-radius: rem(4);

  &__inner {
    @include large-pc {
      padding: rem(16) 0 rem(24);
    }
    @include large-sp {
      padding: rem(12) 0 rem(16);
    }
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__check {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    background: $common-white;
    border: px(1) solid #1f2021;
    border-radius: 4px;
  }

  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    &__title {
      font-family: "Noto Sans JP";
      font-style: normal;
      font-weight: 700;
      font-size: 20px;
      line-height: 125%;
      font-feature-settings: "pwid" on, "palt" on;
      color: #1f2021;

      @include sp {
        font-size: 16px;
      }
    }

    &__button {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 4px;

      &__text {
        font-family: "Noto Sans JP";
        font-style: normal;
        font-weight: 700;
        font-size: 16px;
        line-height: 100%;
        font-feature-settings: "palt" on, "pwid" on;
        color: #1f2021;
        text-align: left;

        @include sp {
          font-size: 14px;
        }
      }
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: rem(170);
    margin-right: rem(-8);
    padding-right: rem(12);
    overflow-x: hidden;
    overflow-y: auto;

    @include large-sp {
      max-height: rem(200);
      margin-right: rem(-16);
      padding-right: rem(16);
    }

    &__item {
      &:not(:last-child) {
        padding-bottom: 10px;
        border-bottom: px(1) solid #cacccf;
      }
    }
  }
}

.p-h01-notifications-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;

  &--clickable {
    cursor: pointer;
  }

  &__detail {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    flex-grow: 1;
    min-width: 0;
  }

  &__date {
    font-family: "Noto Sans JP";
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 100%;
    font-feature-settings: "palt" on, "pwid" on;
    color: #1f2021;

    @include sp {
      font-size: 12px;
    }
  }

  &__body {
    width: 100%;
    font-family: "Noto Sans JP";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 180%;
    font-feature-settings: "palt" on, "pwid" on;
    color: #1f2021;
    overflow-wrap: anywhere;

    @include sp {
      font-size: 12px;
    }
  }

  &__point {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    font-family: "Noto Sans JP";
    font-style: normal;
    font-weight: 700;
    line-height: 100%;
    font-feature-settings: "palt" on, "pwid" on;
    white-space: nowrap;

    &--plus {
      color: #da291c;
    }

    &--minus {
      color: #0d76bf;
    }

    &__sign {
      margin-right: 4px;
      font-size: 18px;

      @include sp {
        font-size: 16px;
      }
    }

    &__num {
      font-size: 18px;

      @include sp {
        font-size: 16px;
      }
    }

    &__unit {
      margin-left: 2px;
      font-size: 12px;
      line-height: 1;

      @include sp {
        font-size: 10px;
      }
    }
  }

  &__read {
    flex-shrink: 0;

    &__button {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }

    &__text {
      font-family: "Noto Sans JP";
      font-style: normal;
      font-weight: 400;
      font-size: 10px;
      line-height: 100%;
      font-feature-settings: "palt" on, "pwid" on;
      color: #1f2021;
      white-space: nowrap;

      @include sp {
        font-size: 8px;
      }
    }
  }
}
</style>
